export type LiveSummaryRiskStateCode =
    | 'STABLE'
    | 'WATCH'
    | 'RECOVERING'
    | 'DEFENSIVE'
    | 'HIGH_RISK'
    | 'CONDITIONS_ALIGNED'
    | 'CONDITIONS_MIXED'
    | 'UNFAVORABLE'
    | 'DATA_LIMITED';

export type LiveSummaryRiskPositionType = 'HOLDING' | 'WATCHLIST' | '';
export type LiveSummaryRiskTone = 'up' | 'down' | 'neutral' | 'warn' | 'good';

export type NormalizedRiskAssessment = {
  available: boolean;
  positionType: LiveSummaryRiskPositionType;
  state: LiveSummaryRiskStateCode;
  stateLabel: string;
  tone: LiveSummaryRiskTone;
  summary: string;
};

export type NormalizedActionDecision = {
  available: boolean;
  policyVersion: string;
  positionType: LiveSummaryRiskPositionType;
  action: string;
  actionLabel: string;
  state: string;
  stateLabel: string;
  tone: LiveSummaryRiskTone;
  headline: string;
  doNow: string;
  doNot: string;
  reasonCodes: string[];
  priceOverextended: boolean;
  reasons: string[];
  positiveTransitionAction: string;
  positiveTransitionHeadline: string;
  positiveTransitionConditions: string[];
  negativeTransitionAction: string;
  negativeTransitionHeadline: string;
  negativeTransitionConditions: string[];
  confidence: number | null;
  evidenceReadyCount: number;
  evidenceTotalCount: number;
  evidenceCoveragePct: number | null;
  evidenceComplete: boolean;
  missingEvidenceCodes: string[];
  missingEvidenceLabels: string[];
};

const STATE_CODES = new Set<LiveSummaryRiskStateCode>([
  'STABLE',
  'WATCH',
  'RECOVERING',
  'DEFENSIVE',
  'HIGH_RISK',
  'CONDITIONS_ALIGNED',
  'CONDITIONS_MIXED',
  'UNFAVORABLE',
  'DATA_LIMITED',
]);

const POSITION_TYPES = new Set<Exclude<LiveSummaryRiskPositionType, ''>>([
  'HOLDING',
  'WATCHLIST',
]);

const TONES = new Set<LiveSummaryRiskTone>([
  'up',
  'down',
  'neutral',
  'warn',
  'good',
]);

const FALLBACK_STATE_LABELS: Record<LiveSummaryRiskStateCode, string> = {
  STABLE: '위험도 안정',
  WATCH: '관찰 중',
  RECOVERING: '흐름 개선',
  DEFENSIVE: '방어 필요도 높음',
  HIGH_RISK: '위험도 매우 높음',
  CONDITIONS_ALIGNED: '진입 조건 충족',
  CONDITIONS_MIXED: '대기 · 신호 엇갈림',
  UNFAVORABLE: '진입 여건 불리',
  DATA_LIMITED: '데이터 확인 중',
};

function text(value: unknown): string {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

function textArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.map(text).filter(Boolean);
}

function number(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(parsed) ? parsed : null;
}

function boundedInt(value: unknown, min: number, max: number): number {
  const parsed = number(value);
  if (parsed === null) return min;
  return Math.max(min, Math.min(max, Math.round(parsed)));
}

function boundedPct(value: unknown): number | null {
  const parsed = number(value);
  if (parsed === null) return null;
  return Math.max(0, Math.min(100, parsed));
}

function stateCode(value: unknown): LiveSummaryRiskStateCode {
  const normalized = text(value).toUpperCase() as LiveSummaryRiskStateCode;
  return STATE_CODES.has(normalized) ? normalized : 'DATA_LIMITED';
}

function positionType(value: unknown): LiveSummaryRiskPositionType {
  const normalized = text(value).toUpperCase() as Exclude<LiveSummaryRiskPositionType, ''>;
  return POSITION_TYPES.has(normalized) ? normalized : '';
}

function tone(value: unknown): LiveSummaryRiskTone {
  const normalized = text(value).toLowerCase() as LiveSummaryRiskTone;
  return TONES.has(normalized) ? normalized : 'neutral';
}

export function emptyRiskAssessment(
    position: LiveSummaryRiskPositionType = '',
): NormalizedRiskAssessment {
  return {
    available: false,
    positionType: position,
    state: 'DATA_LIMITED',
    stateLabel: '데이터 확인 중',
    tone: 'neutral',
    summary: '현재 판단에 필요한 핵심 데이터를 확인하고 있습니다.',
  };
}

export function normalizeRiskAssessment(
    value: unknown,
    expectedPosition: LiveSummaryRiskPositionType = '',
): NormalizedRiskAssessment {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return emptyRiskAssessment(expectedPosition);
  }

  const source = value as Record<string, unknown>;
  const normalizedPosition = positionType(source.positionType) || expectedPosition;
  const state = stateCode(source.state);
  const stateLabel = text(source.stateLabel);
  const summary = text(source.summary);
  const available = Boolean(text(source.state) || stateLabel || summary);

  if (!available) return emptyRiskAssessment(normalizedPosition);

  return {
    available: true,
    positionType: normalizedPosition,
    state,
    stateLabel: stateLabel || FALLBACK_STATE_LABELS[state],
    tone: tone(source.tone),
    summary,
  };
}

export function emptyActionDecision(
    position: LiveSummaryRiskPositionType = '',
): NormalizedActionDecision {
  return {
    available: false,
    policyVersion: '',
    positionType: position,
    action: '',
    actionLabel: '',
    state: '',
    stateLabel: '',
    tone: 'neutral',
    headline: '',
    doNow: '',
    doNot: '',
    reasonCodes: [],
    priceOverextended: false,
    reasons: [],
    positiveTransitionAction: '',
    positiveTransitionHeadline: '',
    positiveTransitionConditions: [],
    negativeTransitionAction: '',
    negativeTransitionHeadline: '',
    negativeTransitionConditions: [],
    confidence: null,
    evidenceReadyCount: 0,
    evidenceTotalCount: 0,
    evidenceCoveragePct: null,
    evidenceComplete: false,
    missingEvidenceCodes: [],
    missingEvidenceLabels: [],
  };
}

/**
 * Backend LiveSummaryActionDecision을 그대로 표시용으로 정규화한다.
 * 프론트에서 ENTRY_READY/WAIT/AVOID를 새로 판정하지 않는다.
 */
export function normalizeActionDecision(
    value: unknown,
    expectedPosition: LiveSummaryRiskPositionType = '',
): NormalizedActionDecision {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return emptyActionDecision(expectedPosition);
  }

  const source = value as Record<string, unknown>;
  const normalizedPosition = positionType(source.positionType) || expectedPosition;
  const action = text(source.action).toUpperCase();
  const headline = text(source.headline);
  // New backend projection exposes only the UI-relevant boolean instead of
  // the internal reason-code taxonomy.  Legacy reasonCodes remain as a
  // temporary fallback so frontend/backend can be deployed independently.
  const legacyReasonCodes = textArray(source.reasonCodes).map((item) => item.toUpperCase());
  const priceOverextended = Boolean(source.priceOverextended)
      || legacyReasonCodes.includes('PRICE_OVEREXTENDED');
  const reasons = textArray(source.reasons);
  const total = boundedInt(source.evidenceTotalCount, 0, 20);
  const ready = boundedInt(source.evidenceReadyCount, 0, total || 20);
  const available = Boolean(action || headline || text(source.state) || reasons.length);

  if (!available) return emptyActionDecision(normalizedPosition);

  return {
    available: true,
    policyVersion: text(source.policyVersion),
    positionType: normalizedPosition,
    action,
    actionLabel: text(source.actionLabel),
    state: text(source.state).toUpperCase(),
    stateLabel: text(source.stateLabel),
    tone: tone(source.tone),
    headline,
    doNow: text(source.doNow),
    doNot: text(source.doNot),
    reasonCodes: [],
    priceOverextended,
    reasons,
    positiveTransitionAction: text(source.positiveTransitionAction).toUpperCase(),
    positiveTransitionHeadline: text(source.positiveTransitionHeadline),
    positiveTransitionConditions: textArray(source.positiveTransitionConditions),
    negativeTransitionAction: text(source.negativeTransitionAction).toUpperCase(),
    negativeTransitionHeadline: text(source.negativeTransitionHeadline),
    negativeTransitionConditions: textArray(source.negativeTransitionConditions),
    confidence: boundedPct(source.confidence),
    evidenceReadyCount: ready,
    evidenceTotalCount: total,
    evidenceCoveragePct: boundedPct(source.evidenceCoveragePct),
    evidenceComplete: Boolean(source.evidenceComplete) && total > 0 && ready === total,
    missingEvidenceCodes: textArray(source.missingEvidenceCodes).map((item) => item.toUpperCase()),
    missingEvidenceLabels: textArray(source.missingEvidenceLabels),
  };
}


export type UserFacingEntryStageCode =
    | 'CONDITIONS_MET'
    | 'APPROACHING'
    | 'WAIT'
    | 'CHASE_RISK'
    | 'UNFAVORABLE'
    | 'DATA_LIMITED';

export type UserFacingEntryStage = {
  code: UserFacingEntryStageCode;
  label: string;
  tone: LiveSummaryRiskTone;
  summary: string;
};

/**
 * WATCHLIST의 사용자용 진입 단계 표현이다.
 * 새로운 매수/매도 판단을 프론트에서 만들지 않고 Backend ActionDecision을
 * 더 직접적인 사용자 언어로만 번역한다.
 *
 * - ENTRY_READY + evidence complete만 '진입 조건 충족'
 * - WAIT/RECOVERING 중 1개 이하 evidence만 남은 경우만 '진입 접근'
 * - PRICE_OVEREXTENDED 근거가 있으면 '추격 부담'
 * - AVOID는 '진입 여건 불리'
 * - 데이터 부족은 fail-closed로 '데이터 확인 중'
 */
export function userFacingEntryStage(
    assessment: NormalizedRiskAssessment,
    decision: NormalizedActionDecision,
): UserFacingEntryStage {
  const summary = decision.doNow || decision.headline || assessment.summary;
  const priceOverextended = decision.priceOverextended;

  if (decision.action === 'REVIEW'
      || decision.state === 'DATA_LIMITED'
      || assessment.state === 'DATA_LIMITED') {
    return {
      code: 'DATA_LIMITED',
      label: '데이터 확인 중',
      tone: 'neutral',
      summary: summary || '진입 판단에 필요한 핵심 데이터를 확인하고 있습니다.',
    };
  }

  if (decision.action === 'ENTRY_READY'
      && decision.evidenceComplete
      && decision.evidenceTotalCount > 0) {
    return {
      code: 'CONDITIONS_MET',
      label: '진입 조건 충족',
      tone: decision.tone === 'neutral' ? 'good' : decision.tone,
      summary: summary || '현재 진입 검토 기준에 필요한 조건이 함께 확인되었습니다.',
    };
  }

  if (priceOverextended) {
    return {
      code: 'CHASE_RISK',
      label: '추격 부담',
      tone: 'warn',
      summary: summary || '종목 흐름과 별개로 현재 가격 이격이 커 추격 부담을 확인해야 합니다.',
    };
  }

  if (decision.action === 'AVOID' || assessment.state === 'UNFAVORABLE') {
    return {
      code: 'UNFAVORABLE',
      label: '진입 여건 불리',
      tone: 'down',
      summary: summary || '현재 가격·수급·시장 조건의 조합은 신규 진입에 불리합니다.',
    };
  }

  const oneConditionLeft = decision.evidenceTotalCount > 0
      && decision.evidenceReadyCount >= decision.evidenceTotalCount - 1;
  if (decision.action === 'WAIT'
      && decision.state === 'RECOVERING'
      && oneConditionLeft) {
    return {
      code: 'APPROACHING',
      label: '진입 접근',
      tone: decision.tone === 'down' ? 'warn' : 'up',
      summary: summary || '조건 대부분이 확인됐고 남은 진입 조건을 기다리는 단계입니다.',
    };
  }

  return {
    code: 'WAIT',
    label: '대기',
    tone: decision.tone === 'down' ? 'warn' : decision.tone,
    summary: summary || assessment.summary || '현재는 진입 조건을 더 기다리는 단계입니다.',
  };
}

/**
 * 보유종목 위험 우선순위용이다.
 * 매수·매도 판단이나 WATCHLIST 후보 순위를 프론트에서 새로 생성하지 않는다.
 */
export function riskAssessmentPriority(assessment: NormalizedRiskAssessment): number {
  switch (assessment.state) {
    case 'HIGH_RISK':
      return 700;
    case 'DEFENSIVE':
      return 600;
    case 'UNFAVORABLE':
      return 500;
    case 'DATA_LIMITED':
      return 400;
    case 'CONDITIONS_MIXED':
      return 300;
    case 'WATCH':
      return 250;
    case 'RECOVERING':
      return 200;
    case 'CONDITIONS_ALIGNED':
      return 150;
    case 'STABLE':
      return 100;
    default:
      return 0;
  }
}

export function isRiskAssessmentAttention(assessment: NormalizedRiskAssessment): boolean {
  return assessment.available
      && ['HIGH_RISK', 'DEFENSIVE', 'UNFAVORABLE', 'DATA_LIMITED'].includes(assessment.state);
}
